import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { Service } from 'webapp-types';

import BoardImagePreviewComponent from 'webapp/components/board/image-preview';
import SubmitButtonComponent from 'webapp/components/submit-button';

import type InfolayerItem from 'webapp/models-custom/infolayer-item/view-board';

interface Args {
	model: InfolayerItem;
	onClose: () => void;
}

export default class InfolayerTypeViewBoardComponent extends Component<Args> {
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('popup') declare popupService: Service.Popup;
	@service('account') declare accountService: Service.Account;

	helpUrl =
		'https://help.databox.com/transitioning-metrics-from-google-universal-analytics-to-google-analytics-4';

	get model(): InfolayerItem {
		return this.args.model;
	}

	get wasMigrationSuccessful(): boolean {
		return this.model.successfullyMigratedSourcesCount !== 0;
	}

	get title(): string {
		if (this.wasMigrationSuccessful) {
			return `‘${this.model.board.name}’ is ready for you.`;
		} else {
			return 'Unable to transition the dashboard';
		}
	}

	get buttonText(): string {
		if (this.wasMigrationSuccessful) {
			return 'View dashboard';
		} else {
			return 'More info';
		}
	}

	get message(): string {
		if (this.wasMigrationSuccessful) {
			return `${this.model.successfullyMigratedSourcesCount} out of ${this.model.sourceCount} Universal Analytics metrics have been successfully transitioned.`;
		} else {
			return 'We were unable to transition any of the Universal Analytics metrics.';
		}
	}

	get showActionButton(): boolean {
		return this.wasMigrationSuccessful || !this.accountService.replaceHelpUrl;
	}

	@action
	viewBoardOrHelp(): void {
		if (this.wasMigrationSuccessful) {
			this.mmBoardsService.openPreview(this.args.model.board);
		} else {
			window.open(this.helpUrl, '_blank');
		}

		this.args.onClose();
	}
	<template>
		<div role="button" class="infolayer infolayer--type-view-board {{@model.classes}}">
			<div>
				{{#if @model.isLoading}}
					<p>Transitioning <strong>"{{@model.board.name}}"</strong></p>
				{{else if @model.isError}}
					<p>
						We've encountered the following error while transitioning to GA4 source with
						<strong>"{{@model.board.name}}"</strong>:
						{{@model.errorText}}
					</p>
				{{else}}
					<div class="board">
						{{#if this.wasMigrationSuccessful}}
							<BoardImagePreviewComponent
								@image={{@model.board.image}}
								@color={{@model.board.color}}
								@theme={{@model.board.bgTheme}}
								@isLazyLoaded={{true}}
							/>
						{{/if}}
						<div class="message">
							<span class="title">{{this.title}}</span>
							<p>{{this.message}}</p>
						</div>
					</div>
					<div class="controls2">
						<div class="controls2__right">
							<a class="close" {{on "click" @onClose}}>Dismiss</a>
							{{#if this.showActionButton}}
								<SubmitButtonComponent
									@text={{this.buttonText}}
									@onTrigger={{this.viewBoardOrHelp}}
								/>
							{{/if}}
						</div>
					</div>
				{{/if}}
			</div>
		</div>
	</template>
}
